/**
 * The raw view of a message: the stored content (or the structured turn) as text. The thread shows the toggle in a
 * message's footer (`RawMessageButton`) and swaps the body for `RawMessageView`; `RawMessageToggle` keeps both in one
 * wrapper for a surface that draws a message itself. Demo page: /ui-elements/buttons (ghost icon button).
 */
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
import { Code2, Eye } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '../ui/Button.js';

export function buildRawMessageInput(content: string, parts: ConversationMessagePart[] = []): string {
  if (parts.length > 0) {
    return JSON.stringify(
      {
        type: 'conversation.assistant_turn',
        version: 1,
        message: content || undefined,
        parts,
      },
      null,
      2,
    );
  }

  return content;
}

interface RawMessageButtonProps {
  showRaw: boolean;
  onToggle: () => void;
}

export function RawMessageButton({ showRaw, onToggle }: RawMessageButtonProps) {
  const label = showRaw ? 'Show rendered message' : 'Show raw message input';
  return (
    <Button variant="ghost" size="sm" iconOnly aria-label={label} title={label} onClick={onToggle}>
      {showRaw ? <Eye size={14} aria-hidden /> : <Code2 size={14} aria-hidden />}
    </Button>
  );
}

export function RawMessageView({ content, parts = [] }: { content: string; parts?: ConversationMessagePart[] }) {
  return (
    <pre className="max-h-80 w-full overflow-auto rounded-md border border-card-border bg-background-gray-secondary_alt px-2.5 py-2 font-mono text-[11px] leading-relaxed wrap-break-word whitespace-pre-wrap text-text-secondary">
      {buildRawMessageInput(content, parts) || '(empty)'}
    </pre>
  );
}

interface RawMessageToggleProps {
  content: string;
  parts?: ConversationMessagePart[];
  children: ReactNode;
}

export function RawMessageToggle({ content, parts = [], children }: RawMessageToggleProps) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="relative">
      <div className="absolute top-1 right-1 z-10">
        <RawMessageButton showRaw={showRaw} onToggle={() => setShowRaw((current) => !current)} />
      </div>
      <div className="pr-8">{showRaw ? <RawMessageView content={content} parts={parts} /> : children}</div>
    </div>
  );
}
